import { Injectable, Inject } from "@angular/core";
import { AccountClient } from "../models";
import { HttpClient } from "@angular/common/http";
import { tap } from "rxjs/operators";
import * as moment from "moment";
import { TokenValue, LoginModel, API_BASE_URL } from "../models";
import { Observable, BehaviorSubject } from "rxjs";
import { Router } from "@angular/router";
import { SessionDataService } from "./session-data.service";

const emptyTokenValue = {
  access_token: "",
  token_type: "",
  expires_in: 0,
  username: "",
  company_id: "",
  name: "",
  logical: "",
  ".issued": "",
  ".expires": "",
  passwordMeetsRequirements: "",
  isCompany: false
};

@Injectable({
  providedIn: "root"
})
export class AuthService
{

  private currentUserInfoSubject: BehaviorSubject<TokenValue>;
  public currentUserInfo: Observable<TokenValue>;

  constructor(
    private http: HttpClient,
    private router: Router,
    private accountClient: AccountClient,
    private sessionDataService: SessionDataService,
    @Inject(API_BASE_URL) private baseUrl?: string
  ) 
  {
    this.currentUserInfoSubject = new BehaviorSubject<TokenValue>(JSON.parse(sessionStorage.getItem("userInfo")));
    this.currentUserInfo = this.currentUserInfoSubject.asObservable();
  }

  public login(loginModel: LoginModel, isCompany: boolean): Observable<TokenValue> 
  {
    this.sessionDataService.flush();
    return this.http.post<TokenValue>(this.baseUrl + "/account/weblogin", loginModel)
      .pipe(tap(res =>
      {
        res.isCompany = isCompany;
        this.setSession(res);
      }));
  }

  public setSession(authResult: TokenValue) 
  {
    const expiresAt = moment().add(authResult.expires_in, "second");
    sessionStorage.setItem("access_token", authResult.access_token);
    sessionStorage.setItem("expires_at", JSON.stringify(expiresAt.valueOf()));
    sessionStorage.setItem("passwordOkay", JSON.stringify(authResult.passwordMeetsRequirements));
    sessionStorage.setItem("userInfo", JSON.stringify(authResult));
    this.currentUserInfoSubject.next(authResult);
  }

  public logout() 
  {
    if (this.isLoggedIn()) 
    {
      this.accountClient.logout();
      sessionStorage.removeItem("access_token");
      sessionStorage.removeItem("expires_at");
      sessionStorage.removeItem("userInfo");
      sessionStorage.removeItem("passwordOkay");

      sessionStorage.clear();
      this.sessionDataService.flush();

      this.logoutRedirect();
      this.currentUserInfoSubject.next(emptyTokenValue);
    }
  }

  public logoutRedirect() 
  {
    const currentUserInfo = this.currentUserInfoSubject.value;
    const navigateTo = currentUserInfo?.isCompany ? "company" : "";

    this.router.navigate([navigateTo], { queryParams: { logical: currentUserInfo?.company_id + currentUserInfo?.logical } });
  }

  public isLoggedIn(): boolean 
  {
    return moment().isBefore(this.getExpiration());
  }

  private getExpiration(): moment.Moment 
  {
    const expiration = sessionStorage.getItem("expires_at");
    const expiresAt = JSON.parse(expiration);
    return moment(expiresAt);
  }

  public getCurrentUserInfo(): TokenValue 
  {
    return this.currentUserInfoSubject.value;
  }
}
